import { useEffect, useRef, useState } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'] as const;

/**
 * Tracks inactivity. `warning` turns true `warnMs` before `timeoutMs`; `onTimeout`
 * fires at `timeoutMs`. Any activity (or `reset()`) starts the clock again.
 */
export function useIdleTimer({
  timeoutMs,
  warnMs,
  onTimeout,
  enabled = true,
}: {
  timeoutMs: number;
  warnMs: number;
  onTimeout: () => void;
  enabled?: boolean;
}) {
  const [warning, setWarning] = useState(false);
  const warningRef = useRef(false);
  const timers = useRef<number[]>([]);
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  });

  const resetRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (!enabled) return undefined;
    const clear = () => {
      timers.current.forEach((t) => {
        window.clearTimeout(t);
      });
      timers.current = [];
    };
    const start = () => {
      clear();
      warningRef.current = false;
      setWarning(false);
      timers.current.push(
        window.setTimeout(() => {
          warningRef.current = true;
          setWarning(true);
        }, timeoutMs - warnMs),
        window.setTimeout(() => {
          onTimeoutRef.current();
        }, timeoutMs),
      );
    };
    resetRef.current = start;
    // While the warning is showing, only the explicit "Stay signed in" resets the clock.
    const onActivity = () => {
      if (warningRef.current) return;
      start();
    };
    start();
    ACTIVITY_EVENTS.forEach((e) => {
      window.addEventListener(e, onActivity, { passive: true });
    });
    return () => {
      clear();
      ACTIVITY_EVENTS.forEach((e) => {
        window.removeEventListener(e, onActivity);
      });
    };
  }, [enabled, timeoutMs, warnMs]);

  return {
    warning,
    reset: () => {
      resetRef.current();
    },
  };
}
